import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { getErrorMessage } from '../api/client';
import { getSubmission, saveSubmissionAnswers, submitSubmission } from '../api/formSubmissions';
import { evaluateFormVisibility } from '../utils/formVisibility';
import { answersFromDetail, answersToPayload, answersToVisibilityInput, type RunnerAnswers } from '../utils/formSubmissionAnswers';
import { answerSummary, isAnswered, missingInSection, optionPresentation, requiredProgress, sectionReason, submitReason } from '../utils/formRunnerGuided';
import { Answers, ChoiceCards, DoneRow, FooterReason, MultiChips, Question } from './capture';
import type { FormAnswerValue } from '../types/forms';
import type { SubmissionDetail, SubmissionQuestion } from '../types/formSubmissions';
import { DEFAULT_STORE_TIMEZONE } from '../utils/timezones';

// Fase 23B: ejecutor de formularios. Muestra las secciones/preguntas publicadas, evalua condiciones en
// vivo con la MISMA semantica que 23A/backend, autosava con debounce y control optimista, y permite
// revisar y enviar. Un SUBMITTED es de solo lectura. Prioridad movil.
// FI-22 · Entrega 3: se responde con el formato de captura guiada — una sección por pantalla, pregunta
// con barra y respuestas con sangría (tarjetas, chips o campos), barra de avance de obligatorias, pie
// callado que dice qué falta, Revisar como filas encogidas con «Cambiar». La lógica pura vive en
// utils/formRunnerGuided.ts; el guardado, las condiciones y el envío no cambian.

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/** Enter cierra el teclado en los campos de una línea. */
function blurOnEnter(event: KeyboardEvent<HTMLInputElement>) {
  if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); }
}

export function FormRunner({ submissionId, onBack, onSubmitted }: { submissionId: string; onBack: () => void; onSubmitted?: (id: string) => void }) {
  const [detail, setDetail] = useState<SubmissionDetail | null>(null);
  const [answers, setAnswers] = useState<RunnerAnswers>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [phase, setPhase] = useState<'fill' | 'review' | 'done'>('fill');
  const [sectionIndex, setSectionIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const dirtyRef = useRef(false);
  const expectedRef = useRef<string | undefined>(undefined);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const rootRef = useRef<HTMLElement>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(undefined);
    try {
      const res = await getSubmission(submissionId);
      setDetail(res.submission);
      setAnswers(answersFromDetail(res.submission));
      expectedRef.current = res.submission.updatedAt;
      if (res.submission.status === 'SUBMITTED') setPhase('done');
    } catch (e) { setError(getErrorMessage(e, 'No se pudo cargar el formulario.')); }
    finally { setLoading(false); }
  }, [submissionId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const visibility = useMemo(() => {
    if (!detail) return null;
    return evaluateFormVisibility(detail, answersToVisibilityInput(answers));
  }, [detail, answers]);

  const doSave = useCallback(async () => {
    if (!detail || detail.status !== 'DRAFT' || !dirtyRef.current) return;
    setSaveState('saving');
    try {
      const res = await saveSubmissionAnswers(submissionId, answersToPayload(detail.questions, answers), expectedRef.current);
      expectedRef.current = res.submission.updatedAt;
      dirtyRef.current = false;
      setSaveState('saved');
    } catch (e) {
      setSaveState('error');
      setError(getErrorMessage(e, 'No se pudo guardar. Tus respuestas siguen aqui; reintenta.'));
    }
  }, [detail, answers, submissionId]);

  const setAnswer = (qid: string, value: FormAnswerValue) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
    dirtyRef.current = true;
    setSaveState('saving');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { void doSave(); }, 800);
  };

  // Obligatorias VISIBLES: una obligatoria oculta por condición no cuenta ni bloquea (misma regla que el backend).
  const requiredVisibleIds = useMemo(() => visibility?.requiredVisibleQuestionIds ?? new Set<string>(), [visibility]);
  const missingRequired = useMemo(() => {
    if (!detail) return [];
    return detail.questions.filter((q) => requiredVisibleIds.has(q.id) && !isAnswered(answers[q.id]));
  }, [detail, requiredVisibleIds, answers]);

  const scrollToTop = () => rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const goToSection = (index: number) => { setSectionIndex(index); scrollToTop(); };
  const goReview = async () => { if (timerRef.current) clearTimeout(timerRef.current); await doSave(); setPhase('review'); scrollToTop(); };

  const doSubmit = async () => {
    if (submitting) return;
    setSubmitting(true); setError(undefined);
    try {
      if (dirtyRef.current) await doSave();
      const res = await submitSubmission(submissionId, expectedRef.current);
      setDetail(res.submission);
      setAnswers(answersFromDetail(res.submission));
      setPhase('done');
      scrollToTop();
      onSubmitted?.(submissionId);
    } catch (e) { setError(getErrorMessage(e, 'No se pudo enviar el formulario.')); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="inline-loading"><span className="spinner" />Cargando formulario…</div>;
  if (!detail) return <div className="alert alert--error" role="alert">{error ?? 'No disponible.'}</div>;

  const readOnly = detail.status === 'SUBMITTED';
  const visibleSections = detail.sections.filter((s) => !visibility || visibility.visibleSectionIds.has(s.id));
  const visibleQuestions = (sectionId: string) => detail.questions.filter((q) => q.sectionId === sectionId && (!visibility || visibility.visibleQuestionIds.has(q.id)));
  const currentIndex = Math.min(sectionIndex, Math.max(0, visibleSections.length - 1));
  const currentSection = visibleSections[currentIndex];
  const isLast = currentIndex >= visibleSections.length - 1;
  const progress = requiredProgress(requiredVisibleIds, answers);
  const stepReason = currentSection ? sectionReason(missingInSection(detail.questions, currentSection.id, requiredVisibleIds, answers)) : null;
  const sendReason = submitReason(missingRequired.length);
  const submittedAtText = detail.submittedAt ? new Date(detail.submittedAt).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone: DEFAULT_STORE_TIMEZONE }) : '—';

  return (
    <section className="form-guided" aria-label={`Formulario ${detail.formName}`} ref={rootRef}>
      <div className="form-guided__header">
        <button className="text-button" onClick={onBack} type="button">← Volver</button>
        <h2>{detail.formName}</h2>
        <span className="form-guided__meta">{detail.storeName} · v{detail.versionNumber}</span>
        {!readOnly && <span className={`form-save form-save--${saveState}`} role="status" aria-live="polite">{saveState === 'saving' ? 'Guardando…' : saveState === 'saved' ? 'Guardado' : saveState === 'error' ? 'No se pudo guardar' : ''}</span>}
      </div>

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {phase === 'done' && (
        <div className="form-guided__done">
          <div className="alert alert--success" role="status"><strong>Formulario enviado ✓</strong> · {detail.storeName} · {submittedAtText} · {detail.respondent.name}</div>
          <p className="form-runner__note">Las respuestas ya no se pueden modificar.</p>
          <div className="form-guided__review">
            {visibleSections.map((section) => (
              <Fragment key={section.id}>
                <div className="form-guided__section">{section.title}</div>
                {visibleQuestions(section.id).map((q) => {
                  const summary = answerSummary(q, answers[q.id]);
                  return <DoneRow key={q.id} label={q.text} value={summary || 'Sin respuesta'} tone={summary ? 'done' : 'empty'} />;
                })}
              </Fragment>
            ))}
          </div>
          <div><button className="button button--primary" onClick={onBack} type="button">Volver</button></div>
        </div>
      )}

      {phase !== 'done' && (
        <div className="wiz-progress" aria-live="polite">
          <div className="wiz-progress__text">
            <span>{phase === 'review' ? 'Revisar' : visibleSections.length > 1 ? `Sección ${currentIndex + 1} de ${visibleSections.length}` : detail.formName}</span>
            <span>{progress.total === 0 ? 'Sin preguntas obligatorias' : `${progress.answered} de ${progress.total} obligatorias contestadas`}</span>
          </div>
          <div className="wiz-progress__bar"><div className={`wiz-progress__fill${progress.answered === progress.total ? ' wiz-progress__fill--ok' : ''}`} style={{ width: `${progress.percent}%` }} /></div>
        </div>
      )}

      {phase === 'fill' && (
        <>
          {currentSection
            ? <>
              <div className="form-guided__section">{currentSection.title}{currentSection.description && <small>{currentSection.description}</small>}</div>
              {visibleQuestions(currentSection.id).map((q) => (
                <Fragment key={q.id}>
                  <Question>{q.text}{!q.isRequired && <span className="form-guided__optional">opcional</span>}</Question>
                  {q.helpText && <p className="wiz-hint">{q.helpText}</p>}
                  <GuidedInput q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
                </Fragment>
              ))}
            </>
            : <p className="form-runner__note">Este formulario no tiene preguntas visibles.</p>}
          <div className="form-guided__footer">
            <FooterReason>{stepReason}</FooterReason>
            {currentIndex > 0 && <button className="button button--ghost" onClick={() => goToSection(currentIndex - 1)} type="button">Atrás</button>}
            <button className="button button--primary form-guided__primary" disabled={stepReason !== null} onClick={() => { if (isLast) void goReview(); else goToSection(currentIndex + 1); }} type="button">{isLast ? 'Revisar y enviar' : 'Siguiente'}</button>
          </div>
        </>
      )}

      {phase === 'review' && (
        <>
          <div className="form-guided__review">
            {visibleSections.map((section, index) => (
              <Fragment key={section.id}>
                <div className="form-guided__section">{section.title}</div>
                {visibleQuestions(section.id).map((q) => {
                  const summary = answerSummary(q, answers[q.id]);
                  const required = requiredVisibleIds.has(q.id);
                  return <DoneRow key={q.id} label={q.text} value={summary || (required ? 'Falta contestar' : 'Sin respuesta')} tone={summary ? 'done' : required ? 'missing' : 'empty'} onChange={() => { setPhase('fill'); goToSection(index); }} />;
                })}
              </Fragment>
            ))}
          </div>
          <div className="form-guided__footer">
            <FooterReason>{sendReason ? `${sendReason} Después de enviar, las respuestas ya no se pueden cambiar.` : 'Después de enviar, las respuestas ya no se pueden cambiar.'}</FooterReason>
            <button className="text-button" disabled={submitting} onClick={() => { setPhase('fill'); goToSection(0); }} type="button">Volver a editar</button>
            <button className="button button--primary form-guided__primary" disabled={submitting || sendReason !== null} onClick={() => void doSubmit()} type="button">{submitting ? 'Procesando…' : 'Enviar formulario'}</button>
          </div>
        </>
      )}
    </section>
  );
}

/** Una pregunta con el formato de captura guiada: tarjetas, chips o campo, según el tipo y cuántas opciones tiene. */
function GuidedInput({ q, value, onChange }: { q: SubmissionQuestion; value: FormAnswerValue; onChange: (v: FormAnswerValue) => void }) {
  switch (q.type) {
    case 'YES_NO':
      return <ChoiceCards ariaLabel={q.text} options={[{ value: 'yes', label: 'Sí' }, { value: 'no', label: 'No' }]} value={value === true ? 'yes' : value === false ? 'no' : ''} onChange={(choice) => onChange(choice === 'yes')} />;
    case 'SINGLE_SELECT': {
      const presentation = optionPresentation(q.options.length);
      const selected = typeof value === 'string' ? value : '';
      // P-078 (accesibilidad, Codex R1): cada control lleva el texto de la pregunta como nombre accesible (antes el
      // `label.field` vacío no le daba ninguno). Mismo control, misma respuesta.
      if (presentation === 'select') {
        return (
          <Answers>
            <label className="field">
              <select aria-label={q.text} onChange={(e) => onChange(e.target.value || null)} value={selected}>
                <option value="">Elige una opción</option>
                {q.options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </label>
          </Answers>
        );
      }
      return <ChoiceCards ariaLabel={q.text} options={q.options.map((o) => ({ value: o.id, label: o.label }))} value={selected} variant={presentation} onChange={(id) => onChange(id)} />;
    }
    case 'MULTI_SELECT':
      return <MultiChips ariaLabel={q.text} options={q.options.map((o) => ({ value: o.id, label: o.label }))} values={Array.isArray(value) ? value : []} onChange={(ids) => onChange(ids)} />;
    case 'NUMBER':
      return <Answers><label className="field wiz-big-input"><input aria-label={q.text} enterKeyHint="done" inputMode="decimal" onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} onKeyDown={blurOnEnter} placeholder="0" step="any" type="number" value={typeof value === 'number' ? value : ''} /></label></Answers>;
    case 'SHORT_TEXT':
      return <Answers><label className="field"><input aria-label={q.text} enterKeyHint="done" maxLength={500} onChange={(e) => onChange(e.target.value)} onKeyDown={blurOnEnter} placeholder="Escribe aquí" value={typeof value === 'string' ? value : ''} /></label></Answers>;
    case 'LONG_TEXT':
      return <Answers><label className="field"><textarea aria-label={q.text} maxLength={5000} onChange={(e) => onChange(e.target.value)} placeholder="Escribe aquí" rows={3} value={typeof value === 'string' ? value : ''} /></label></Answers>;
    case 'DATE':
      return <Answers><label className="field"><input aria-label={q.text} onChange={(e) => onChange(e.target.value)} type="date" value={typeof value === 'string' ? value : ''} /></label></Answers>;
    case 'TIME':
      return <Answers><label className="field"><input aria-label={q.text} onChange={(e) => onChange(e.target.value)} type="time" value={typeof value === 'string' ? value : ''} /></label></Answers>;
    default: return null;
  }
}
